/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Divider, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import Link from 'components/ChoreoSystem/Link/Link';
import { choreoHomePage, privacyPolicy, termsOfUse } from 'config';
import useStyles from './PublicFooter.styles';

/**
 * The marketing site shown as a bare domain, the way the design has it.
 *
 * Derived from the URL rather than written out beside it, so the label cannot
 * drift from where the link actually goes.
 */
const homePageLabel = (choreoHomePage || '')
  .replace(/^https?:\/\//, '')
  .replace(/\/$/, '');

/**
 * The footer of the unauthenticated landing page: where to read more, and the
 * two policy links every page of the product carries.
 */
export default function PublicFooter() {
  const classes = useStyles();

  return (
    <Box component="footer" className={classes.publicFooter}>
      {/*
        Only with a home page to point at. A deployment that omits the key
        would otherwise render "More details at " followed by an empty,
        focusable link going nowhere — the line has nothing to say without it.
      */}
      {choreoHomePage && (
        <Typography variant="body1" color="textSecondary">
          <FormattedMessage
            id="modules.login.publicFooter.moreDetails"
            defaultMessage="More details at {link}"
            values={{
              link: (
                <Link
                  testId="public-footer-home-page"
                  href={choreoHomePage}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body1"
                >
                  {homePageLabel}
                </Link>
              ),
            }}
          />
        </Typography>
      )}

      <Box className={classes.policyLinks}>
        <Link
          testId="public-footer-privacy-policy"
          href={privacyPolicy}
          target="_blank"
          rel="noopener noreferrer"
          variant="body1"
          color="textSecondary"
          underline="none"
        >
          <FormattedMessage
            id="layouts.main.components.Footer.privacy.policy"
            defaultMessage="Privacy Policy"
          />
        </Link>
        <Divider orientation="vertical" light flexItem />
        <Link
          testId="public-footer-terms-of-use"
          href={termsOfUse}
          target="_blank"
          rel="noopener noreferrer"
          variant="body1"
          color="textSecondary"
          underline="none"
        >
          <FormattedMessage
            id="layouts.main.components.Footer.terms.of.use"
            defaultMessage="Terms of Use"
          />
        </Link>
      </Box>
    </Box>
  );
}
